/**
 * Body of `handleSelectGmailPerson` extracted verbatim from
 * `ContactEditModal.tsx` and parameterized on a typed deps object.
 *
 * The orchestrator keeps the `useCallback` shell (with its byte-identical `[]`
 * dependency array) and delegates here, so reference identity and hook order
 * are unchanged (behavior-preserving — #3293 Pass-2).
 *
 * When a user picks a Gmail/Google-Contacts person in the freeform new-contact
 * picker this:
 *   1. IMMEDIATELY fills name/email/company from the domain + Google Contacts;
 *   2. shows the enrichment spinner;
 *   3. in the BACKGROUND, runs signature-parse + web-enrichment in parallel and
 *      streams the results into the form as each completes.
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { getAuthItem } from '@/lib/authStorage';
import { emailApiBase } from '@/lib/email-api';
import type { GoogleContact } from './gmailPeopleSearch';

export interface SelectGmailPersonDeps {
  googleContactsRef: MutableRefObject<GoogleContact[]>;
  setGmailResults: Dispatch<SetStateAction<Array<{ email: string; name: string; photoUrl: string | null; score: number }>>>;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setName: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setFreeformText: Dispatch<SetStateAction<string>>;
  setFreeformParsed: Dispatch<SetStateAction<Record<string, any> | null>>;
  setCompany: Dispatch<SetStateAction<string>>;
  setFirm: Dispatch<SetStateAction<string>>;
  setPhone: Dispatch<SetStateAction<string>>;
  setContactTitle: Dispatch<SetStateAction<string>>;
  setEnrichLoading: Dispatch<SetStateAction<boolean>>;
  setXHandle: Dispatch<SetStateAction<string>>;
  setLinkedinUrl: Dispatch<SetStateAction<string>>;
  setSummary: Dispatch<SetStateAction<string>>;
  setLocation: Dispatch<SetStateAction<string>>;
  setEnrichmentSummary: Dispatch<SetStateAction<string>>;
  setEnrichmentSkills: Dispatch<SetStateAction<string[]>>;
  setEnrichmentConfidence: Dispatch<SetStateAction<string>>;
}

export function handleSelectGmailPerson(
  person: { email: string; name: string },
  deps: SelectGmailPersonDeps,
): void {
  const {
    googleContactsRef, setGmailResults, setFirstName, setLastName, setName, setEmail,
    setFreeformText, setFreeformParsed, setCompany, setFirm, setPhone, setContactTitle,
    setEnrichLoading, setXHandle, setLinkedinUrl, setSummary, setLocation,
    setEnrichmentSummary, setEnrichmentSkills, setEnrichmentConfidence,
  } = deps;

  const nameParts = person.name.split(' ');
  setGmailResults([]);

  // Google Contacts data
  const personEmailLower = (person.email || '').toLowerCase();
  const personNameLower = person.name.toLowerCase();
  const googleMatch = googleContactsRef.current.find(gc =>
    (personEmailLower && gc.email?.toLowerCase() === personEmailLower)
    || gc.name?.toLowerCase() === personNameLower
  );
  const resolvedEmail = person.email || googleMatch?.email || '';

  // --- IMMEDIATE: show name, email, company from domain/Google Contacts ---
  setFirstName(nameParts[0] || '');
  setLastName(nameParts.slice(1).join(' ') || '');
  setName(person.name);
  setEmail(resolvedEmail);
  setFreeformText(person.name);
  setFreeformParsed({ firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') || '' });

  // Company from domain
  const genericDomains = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','me.com','mail.com','protonmail.com','proton.me','live.com','msn.com','ymail.com','comcast.net','att.net','verizon.net','sbcglobal.net','cox.net','charter.net']);
  let domainCompany = '';
  if (resolvedEmail) {
    const domain = resolvedEmail.split('@')[1]?.toLowerCase();
    if (domain && !genericDomains.has(domain)) {
      const bizWords = ['partners','capital','ventures','group','holdings','labs','tech','digital','media','health','solutions','consulting','systems','global','financial','advisors','management','services','corp','inc','llc','studio','agency','design','software','network','energy'];
      let raw = domain.split('.')[0].replace(/[-_]/g, ' ');
      for (const bw of bizWords) {
        const idx = raw.toLowerCase().indexOf(bw);
        if (idx > 0 && idx + bw.length === raw.length) { raw = raw.slice(0, idx) + ' ' + raw.slice(idx); break; }
      }
      domainCompany = raw.replace(/\b\w/g, c => c.toUpperCase());
    }
  }
  const initialCompany = googleMatch?.organization || domainCompany || '';
  if (initialCompany) { setCompany(initialCompany); setFirm(initialCompany); }
  if (googleMatch?.phone) setPhone(googleMatch.phone);
  if (googleMatch?.title) setContactTitle(googleMatch.title);

  // Show enrichment loading indicator (but card is already visible with basic info)
  setEnrichLoading(true);

  // --- BACKGROUND: progressive enrichment ---
  (async () => {
    const { apiPost } = await import('@/lib/api/client');
    const authToken = getAuthItem('xavior_auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

    // Signature parse and web enrichment run in PARALLEL — update state as each completes
    let sigData: Record<string, any> | null = null;

    await Promise.allSettled([
      // 1. Email signature → updates phone, title, company immediately
      (async () => {
        if (!resolvedEmail) return;
        try {
          const searchRes = await fetch(`${emailApiBase()}/search`, {
            method: 'POST', headers,
            body: JSON.stringify({ query: `from:${resolvedEmail}`, max: 3 }),
          });
          if (!searchRes.ok) return;
          const searchData = await searchRes.json();
          const threads: Array<{ id: string }> = searchData.threads || [];
          if (!threads.length) return;
          const threadRes = await fetch(`${emailApiBase()}/thread`, {
            method: 'POST', headers,
            body: JSON.stringify({ threadId: threads[0].id }),
          });
          if (!threadRes.ok) return;
          const threadData = await threadRes.json();
          const fromMsg = (threadData.messages || []).find((m: any) => (m.from || '').toLowerCase().includes(resolvedEmail.toLowerCase()));
          if (!fromMsg) return;
          const { data } = await apiPost<Record<string, any>>('/contacts/parse', { text: fromMsg.body || '' });
          sigData = data;
          // Update immediately as signature data arrives
          if (data) {
            if (data.phone) setPhone(data.phone);
            if (data.company) { setCompany(data.company); setFirm(data.company); }
            if (data.title) setContactTitle(data.title);
          }
        } catch { /* ignore */ }
      })(),
      // 2. Web enrichment → updates AI profile, LinkedIn, X handle, skills
      (async () => {
        try {
          const hintParts = [googleMatch?.organization, domainCompany, resolvedEmail.split('@')[1]].filter(Boolean);
          const knownData: Record<string, string> = {};
          if (resolvedEmail) knownData.email = resolvedEmail;
          if (googleMatch?.organization) knownData.organization = googleMatch.organization;
          if (googleMatch?.title) knownData.title = googleMatch.title;

          const { data } = await apiPost<{ confidence: string; fields: Record<string, any>; rawSummary: string }>('/contacts/enrich', {
            name: person.name,
            hints: [...new Set(hintParts)].join(', ') || undefined,
            knownData: Object.keys(knownData).length > 0 ? knownData : undefined,
          });
          // Update immediately as enrichment arrives
          if (data?.fields) {
            if (data.fields.twitterHandle) setXHandle(data.fields.twitterHandle.replace(/^@/, ''));
            if (data.fields.linkedinUrl) setLinkedinUrl(data.fields.linkedinUrl);
            if (data.fields.bio) setSummary(data.fields.bio);
            if (data.fields.location) setLocation(data.fields.location);
            if (data.fields.company) { setCompany(data.fields.company); setFirm(data.fields.company); }
            if (data.fields.contactTitle) setContactTitle(data.fields.contactTitle);
            if (data.fields.phone) setPhone((prev: string) => prev || data.fields.phone);
          }
          if (data?.rawSummary) setEnrichmentSummary(data.rawSummary);
          if (data?.fields?.skills?.length) setEnrichmentSkills(data.fields.skills);
          if (data?.confidence) setEnrichmentConfidence(data.confidence);
        } catch { /* ignore */ }
      })(),
    ]);

    setEnrichLoading(false);
  })();
}
